import { useEffect, useMemo, useState } from "react";
import { useDraft, clearDraft } from "@/lib/draft";
import { api, formatApiError } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, PenLine, X } from "lucide-react";

export const ZONES = [
  { k: "frente", l: "Frente", x: 45, y: 0, w: 110, h: 60 },
  { k: "teto", l: "Teto", x: 45, y: 64, w: 110, h: 172 },
  { k: "traseira", l: "Traseira", x: 45, y: 240, w: 110, h: 60 },
  { k: "lateral_esq", l: "Lateral esq.", x: 0, y: 30, w: 41, h: 240 },
  { k: "lateral_dir", l: "Lateral dir.", x: 159, y: 30, w: 41, h: 240 },
];
export const DMG = { risco: "Risco", amassado: "Amassado", quebrado: "Quebrado" };
const FUELS = ["0", "1/4", "1/2", "3/4", "cheio"];

export function CarDiagram({ damages, onTap }) {
  const tap = (z) => (e) => {
    if (!onTap) return;
    const r = e.currentTarget.getBoundingClientRect();
    onTap({ zone: z.k, x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) });
  };
  return (
    <svg viewBox="0 0 200 300" className="w-full max-w-[220px] mx-auto select-none" data-testid="car-diagram">
      {ZONES.map((z) => (
        <g key={z.k} onClick={tap(z)} className={onTap ? "cursor-pointer" : ""} data-testid={`zone-${z.k}`}>
          <rect x={z.x} y={z.y} width={z.w} height={z.h} rx="14" fill="rgba(148,163,184,0.18)" stroke="rgba(148,163,184,0.7)" strokeWidth="1.5" />
          <text x={z.x + z.w / 2} y={z.y + z.h / 2} textAnchor="middle" dominantBaseline="middle" fontSize="11" fill="rgba(100,116,139,0.9)" transform={z.w < 50 ? `rotate(-90 ${z.x + z.w / 2} ${z.y + z.h / 2})` : undefined}>{z.l}</text>
        </g>
      ))}
      {damages.map((d, i) => {
        const z = ZONES.find((q) => q.k === d.zone) || ZONES[1];
        const cx = z.x + z.w * (d.x ?? 0.5), cy = z.y + z.h * (d.y ?? 0.5);
        return <g key={i} pointerEvents="none"><circle cx={cx} cy={cy} r="9" fill="#dc2626" stroke="#fff" strokeWidth="1.5" /><text x={cx} y={cy + 0.5} textAnchor="middle" dominantBaseline="middle" fontSize="10" fontWeight="700" fill="#fff">{i + 1}</text></g>;
      })}
    </svg>
  );
}

function DamagePicker({ pending, onAdd, onCancel }) {
  const [note, setNote] = useState("");
  if (!pending) return null;
  return (
    <div data-testid="damage-picker" className="rounded-xl border border-zinc-800 bg-zinc-900 p-3 space-y-2">
      <div className="text-sm text-zinc-200">Avaria em <b>{ZONES.find((z) => z.k === pending.zone)?.l}</b></div>
      <Input data-testid="damage-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Observação (opcional)" className="bg-zinc-950 border-zinc-800" />
      <div className="flex flex-wrap gap-2">
        {Object.entries(DMG).map(([k, l]) => <Button key={k} data-testid={`damage-type-${k}`} size="sm" onClick={() => { onAdd({ ...pending, type: k, note: note || null }); setNote(""); }} className="bg-red-600 hover:bg-red-500 text-white">{l}</Button>)}
        <Button size="sm" variant="ghost" onClick={onCancel} className="text-zinc-400">Cancelar</Button>
      </div>
    </div>
  );
}

export function InspectionCard({ order, onSaved }) {
  const i0 = order.inspection || {};
  const [damages, setDamages] = useState(i0.damages || []);
  const [fuel, setFuel] = useState(i0.fuel || "");
  const [items, setItems] = useState(i0.items || {});
  const [other, setOther] = useState(i0.other_items || "");
  const [labels, setLabels] = useState([]);
  const [pending, setPending] = useState(null);
  const [sigOpen, setSigOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/inspection-items").then(({ data }) => setLabels(data.items)).catch(() => setLabels([])); }, []);
  const draftValue = useMemo(() => ({ damages, fuel, items, other }), [damages, fuel, items, other]);
  useDraft({ orderId: order.id, kind: "inspection", value: draftValue, onRestore: (d) => {
    if (!d) return;
    setDamages(d.damages || []); setFuel(d.fuel || ""); setItems(d.items || {}); setOther(d.other || "");
  } });
  const save = async (signature_png) => {
    setBusy(true);
    try {
      await api.put(`/orders/${order.id}/inspection`, { damages, fuel: fuel || null, items, other_items: other, signature_png });
      clearDraft(order.id, "inspection");
      toast.success(signature_png ? "Vistoria assinada e salva" : "Vistoria salva"); setSigOpen(false); onSaved?.();
    } catch (e) { toast.error(`${formatApiError(e)} — seus dados continuam aqui, tente de novo.`); } finally { setBusy(false); }
  };
  return (
    <div data-testid="inspection-card" className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase tracking-widest"><ClipboardCheck className="h-4 w-4" /> Vistoria de entrada</div>
        {i0.signed_at && <span data-testid="inspection-signed" className="text-[11px] text-green-300">Assinada pelo cliente</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-5">
        <div className="space-y-2">
          <CarDiagram damages={damages} onTap={setPending} />
          <p className="text-[11px] text-zinc-500 text-center">Toque no desenho para marcar uma avaria</p>
          <DamagePicker pending={pending} onAdd={(d) => { setDamages((p) => [...p, d]); setPending(null); }} onCancel={() => setPending(null)} />
          {damages.map((d, i) => (
            <div key={i} className="flex items-center justify-between text-xs text-zinc-300 border-b border-zinc-800 py-1">
              <span><b className="text-red-400">{i + 1}.</b> {ZONES.find((z) => z.k === d.zone)?.l} · {DMG[d.type]}{d.note ? ` — ${d.note}` : ""}</span>
              <button type="button" data-testid={`damage-remove-${i}`} onClick={() => setDamages((p) => p.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          <div><div className="text-[11px] text-zinc-400 mb-1">Combustível</div>
            <div className="flex gap-1">{FUELS.map((f) => <button key={f} type="button" data-testid={`fuel-${f.replace("/", "-")}`} onClick={() => setFuel(f)} className={`flex-1 h-10 rounded-lg border text-sm font-bold ${fuel === f ? "bg-green-brand text-white border-transparent" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>{f === "cheio" ? "Cheio" : f}</button>)}</div></div>
          <div><div className="text-[11px] text-zinc-400 mb-1">Itens no veículo</div>
            <div className="grid grid-cols-2 gap-2">{labels.map((l) => (
              <label key={l} className="flex items-center gap-2 text-sm text-zinc-200"><input type="checkbox" data-testid={`inspection-item-${l}`} checked={!!items[l]} onChange={(e) => setItems((p) => ({ ...p, [l]: e.target.checked }))} className="h-4 w-4 accent-green-600" /> {l}</label>
            ))}</div>
            <Textarea data-testid="inspection-other" value={other} onChange={(e) => setOther(e.target.value)} placeholder="Outros itens / observações" className="mt-2 bg-zinc-900 border-zinc-800 text-sm min-h-[60px]" /></div>
          <p className="text-[11px] text-zinc-500">As fotos de entrada ficam na seção "Fotos de entrada" desta OS.</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" data-testid="inspection-save" onClick={() => save(null)} disabled={busy} variant="outline" className="bg-zinc-900 border-zinc-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar vistoria"}</Button>
            <Button type="button" data-testid="inspection-sign" onClick={() => setSigOpen(true)} disabled={busy} className="bg-green-brand text-white font-bold"><PenLine className="h-4 w-4 mr-1" /> Cliente assina</Button>
          </div>
        </div>
      </div>
      <SignaturePad open={sigOpen} onOpenChange={setSigOpen} onConfirm={save} busy={busy} customerName={order.customer_name} />
    </div>
  );
}
